import "./OCRMSADevelopmentModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { formatDate } from "components/Documents/utils/formatters";
import { useMemo, useState } from "react";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { useGetDocumentsQuery } from "services/documents-api";
import {
  useGetMSAByIdQuery,
  useSubmitOCRMSADevelopmentMutation,
} from "services/msa-api";
import type { BackgroundJobDataType } from "types/DataTypes";

import { KaboodleToaster } from "../common";
import KaboodleModal from "./KaboodleModal";

const isJobFailed = (job: BackgroundJobDataType) =>
  job.status === "Failed" || job.status === "Error";

type OCRMSADevelopmentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  msaId: string;
};

const OCRMSADevelopmentModal = ({
  isOpen,
  onClose,
  msaId,
}: OCRMSADevelopmentModalProps) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );

  const [submitOCRMSADevelopment, { isLoading }] =
    useSubmitOCRMSADevelopmentMutation();

  const { data: msaData } = useGetMSAByIdQuery({ msaId }, { skip: !isOpen });
  const fileIdString = msaData?.file?.id?.toString() || "";

  const { data: documentsData, isLoading: isDocumentsLoading } =
    useGetDocumentsQuery(
      { fileId: fileIdString, sortBy: "-created", limit: "1000", offset: "0" },
      { skip: !fileIdString || !isOpen },
    );

  const { data: jobsResponse } = useGetBackgroundJobsQuery(
    { filters: { msa_id: msaId } },
    { skip: !msaId || !isOpen, refetchOnMountOrArgChange: true },
  );

  // A document being actively scanned has no msa_development yet (that only
  // gets set once OCR finishes), so it would otherwise still look eligible
  // here and 400 on submit. Only an in-flight job holds a document back: a
  // failed one never attached it, and a succeeded one either still has it
  // attached (already filtered out below) or its card was since deleted, in
  // which case the document must be selectable again.
  const documentIdsInProgress = useMemo(
    () =>
      new Set(
        (jobsResponse?.results || [])
          .filter(
            (job) =>
              job.job_type_value === "ocr_msa_development" &&
              !isJobFailed(job) &&
              job.status !== "Success",
          )
          .map((job) => job.document_id)
          .filter((id): id is number => id != null),
      ),
    [jobsResponse],
  );

  // No .pdf extension check: a document's type is what makes it eligible,
  // not its filename. Not yet attached to a development is what makes it
  // still open to process, whether it's never been scanned or a prior scan
  // on it failed.
  const eligibleDocuments = useMemo(
    () =>
      (documentsData?.results || []).filter(
        (doc) =>
          doc.type === "MSA_DEVELOPMENT_LETTER" &&
          doc.msa_development == null &&
          doc.file_size_in_bytes != null &&
          !documentIdsInProgress.has(doc.id),
      ),
    [documentsData, documentIdsInProgress],
  );

  const handleSubmit = async () => {
    if (!selectedDocumentId) {
      KaboodleToaster.error("Please select a document to process");
      return;
    }

    try {
      await submitOCRMSADevelopment({
        msaId,
        documentId: selectedDocumentId,
      }).unwrap();

      KaboodleToaster.success(
        "Document queued for OCR processing. A Development card will be added automatically once it's done.",
      );
      handleClose();
    } catch (error) {
      KaboodleToaster.error(
        error?.data?.error ||
          error?.message ||
          "Failed to submit document. Please try again.",
      );
    }
  };

  const handleClose = () => {
    setSelectedDocumentId(null);
    onClose();
  };

  const isBusy = isLoading;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleSubmit}
            disabled={isBusy || !selectedDocumentId}
          >
            {isBusy ? "Submitting..." : "Submit"}
          </Button>
          <Button onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Process CMS Development Letter">
        <div className="OCRMSADevelopmentModal">
          <div className="data-values">
            {isDocumentsLoading ? (
              <div>Loading documents...</div>
            ) : eligibleDocuments.length === 0 ? (
              <p>No MSA Development Letter documents found for this file.</p>
            ) : (
              <>
                <label>CMS Development Letter</label>
                <div className="ocr-document-list">
                  <div className="ocr-document-row ocr-document-row--header">
                    <span />
                    <span>Document</span>
                    <span>Uploaded</span>
                  </div>
                  {eligibleDocuments.map((doc) => (
                    <label
                      key={doc.id}
                      className="ocr-document-row"
                      htmlFor={`ocr-development-document-${doc.id}`}
                    >
                      <input
                        type="radio"
                        id={`ocr-development-document-${doc.id}`}
                        name="ocr-msa-development-document"
                        checked={selectedDocumentId === doc.id}
                        onChange={() => setSelectedDocumentId(doc.id)}
                      />
                      <span>
                        {doc.original_filename ||
                          doc.original_filename_as_uploaded}
                      </span>
                      <span>{formatDate(doc.created)}</span>
                    </label>
                  ))}
                </div>
                <p>
                  This document will be OCR-ed and a Development card will be
                  filled in with the results.
                </p>
              </>
            )}
          </div>
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default OCRMSADevelopmentModal;
